// 🔧 manifest.js
// ✅ Purpose: Car Show industry manifest for the LGV Seed Engine
// ✅ Features:
// - Aggregates all templates required for a complete Car Show seed
// - Provides a single export consumed by the Industry Registry
// - Makes adding new industries simple and consistent
// - Future-ready for add-ons, AI content, and premium templates
// - Fully block-commented for clarity and future devs

import { CAR_SHOW_CATEGORIES } from "./categories";
import { CAR_SHOW_ENTRIES } from "./entries";

export const CAR_SHOW_MANIFEST = {
  key: "car_show",

  enabled: true,

  label: "Car Show",

  emoji: "🚗",

  entryLabel: "Vehicle",

  categoryLabel: "Class",

  voterLabel: "Car Show Attendee",

  userProfiles: [
    { displayName: "Derek Martinez", participantType: "Show Director" },
    { displayName: "Monica Reynolds", participantType: "Car Show Attendee" },
    { displayName: "Trevor Campbell", participantType: "Car Show Attendee" },
    { displayName: "Jasmine Flores", participantType: "Car Show Attendee" },
    { displayName: "Evan Richardson", participantType: "Car Show Attendee" },
    { displayName: "Brooke Sullivan", participantType: "Car Show Attendee" },
  ],

  description:
    "Creates a realistic car show demo with vehicles, classes, voters, and vote-ready data.",

  version: 1,

  experience: {
    experienceType: "car_show",
    demoAudience: "Car show organizers, entrants, judges, and attendees",
    demoNarrative:
      "A realistic car show Experience demonstrating registration, categories, voting, results, and event operations.",
    participantType: "Vehicle Owner",
    voterType: "Car Show Attendee",
  },

  categories: CAR_SHOW_CATEGORIES,

  // ✅ Compatibility metadata used by shell/report code while the
  // Experience Factory transitions fully to manifest-driven industries.
  defaultCategories: CAR_SHOW_CATEGORIES.map(
    (category) => category.name
  ),

  entries: CAR_SHOW_ENTRIES,

  content: {
    description: `
      <p><strong>Welcome to a full-featured Let's Go Vote car show experience.</strong></p>
      <p>Walk the show field, discover remarkable vehicles, meet their owners, and explore classes ranging from preserved classics to custom builds. Every vehicle includes realistic details so attendees can experience how a live show feels.</p>
      <p>Use the event schedule and map to plan your visit, recognize the sponsors and volunteers who make the show possible, and cast mobile votes without paper ballots or manual counting.</p>
    `,
    participationTips: `
      <p><strong>Car Show Participation and Voting Tips</strong></p>
      <ul>
        <li>Walk each class before deciding which vehicle earns your vote.</li>
        <li>Open a vehicle profile or scan its display QR code to review photos and build details.</li>
        <li>Vote once in every available class, then review your choices from My Ballot.</li>
        <li>Choose Best in Show after comparing vehicles across the entire show field.</li>
        <li>Visit sponsor displays, volunteer stations, the event map, and scheduled activities throughout the day.</li>
      </ul>
    `,
    optionalDetails1: `
      <p><strong>Show-Day Experience</strong></p>
      <p>Start at registration, pick up the event schedule, and use the map to locate vehicle classes, sponsor displays, refreshments, and guest services. Vehicle owners are encouraged to share the stories behind their builds with attendees.</p>
    `,
    optionalDetails2: `
      <p><strong>Awards and Results</strong></p>
      <p>Class awards and Best in Show results are calculated from eligible ballots and released according to the event schedule. Sponsor awards recognize standout vehicles selected by participating event partners.</p>
    `,
    feedbackNote: `
      <p>Tell us about your favorite vehicles, the voting experience, and what you would like to see at the next show. Your feedback helps improve future events for owners, spectators, sponsors, and volunteers.</p>
    `,
    contactNote: `
      <p><strong>Contact the Event Promoter</strong></p>
      <p>Questions about parking, registration, judging, or your volunteer assignment can be directed to the event promoter listed on the event page. On show day, report to the Volunteer Check-In tent near registration.</p>
    `,
    volunteerGoodies: `
      <p>Volunteers receive a show credential, event T-shirt, meal voucher, refreshments, and access to the volunteer appreciation drawing while supplies last.</p>
    `,
    volunteerThanks: `
      <p><strong>Thank you for helping make the show possible!</strong></p>
      <p>Your support keeps vehicle check-in moving, helps guests find their way, assists with awards, and creates a welcoming experience for every participant.</p>
    `,
    sponsorsNote: `
      <p><strong>Put your brand in front of car enthusiasts.</strong></p>
      <p>Car show sponsorship opportunities include recognition on the event website, show-field signage, social promotion, award presentation opportunities, and direct engagement with vehicle owners and attendees. Contact the event promoter to select the best level for your organization.</p>
    `,
    vendorsNote: `
      <p><strong>Explore the car show marketplace.</strong></p>
      <p>Meet local parts suppliers, restoration specialists, detailers, food vendors, and automotive organizations throughout the show field. Check each vendor's booth number and map location to plan your visit.</p>
    `,
  },

  seo: {},

  map: {},

  schedule: {},

  defaults: {
    entryCount: 50,

    userCount: 150,

    voteCount: 750,
  },
};
